import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IProductDataV3 } from '@df/catalog/product/product';
import { Client } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { Exponea } from '@df/module-exponea/exponea.types';
import { TimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { NewsletterService } from '@df/session/newsletter/newsletter.service';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { AbstractFormComponent } from '@df/ui/form/abstract-form.component';
import { ModalService } from '@df/ui/modal/modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ProductDecorator } from '../../catalog/model/product/product.decorator';
import { ActionComponent } from '../../ui/api/action.component';
import { ResultComponent } from '../../ui/api/result.component';
import { CheckboxComponent } from '../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../ui/form/input-wrap.component';
import { MuiInputDirective } from '../../ui/form/mui-input.directive';
import { ExponeaService } from './exponea.service';

@Component({
    selector: 'exponea-stock-notification',
    templateUrl: './exponea-stock-notification.html',
    styleUrl: './exponea-stock-notification.scss',
    standalone: true,
    imports: [
        ActionComponent,
        CheckboxComponent,
        CommonModule,
        FormsModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        MuiInputDirective,
        OptionLabelPipe,
        ResultComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExponeaStockNotificationComponent extends AbstractFormComponent<Exponea.IStockNotificationData> {
    isSuccess = false;

    @Input()
    isSimple?: boolean;

    @Input()
    model!: ProductDecorator<IProductDataV3>;

    @Output()
    openSizeTray = new EventEmitter<boolean>();

    @Output()
    closeSizeTray = new EventEmitter<boolean>();

    @Output()
    closeSizeModal = new EventEmitter<boolean>();

    protected submitPath = 'lead_watchdog_stock';

    private exponeaConsentStatus = new Status();

    private exponeaConsentResult = false;

    private checkExponeaConsentDebounced!: (email: string) => void;

    constructor(
        ref: ChangeDetectorRef,
        public client: Client,
        private modalService: ModalService,
        private exponeaService: ExponeaService,
        private timeoutService: TimeoutService,
        private customerModel: CustomerModel,
        private newsletterService: NewsletterService
    ) {
        super(ref);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.checkExponeaConsentDebounced = this.timeoutService.debounce((email: string) => this.checkExponeaConsent(email), 1000);
        this.subscriptions.add(this.exponeaConsentStatus.subscribe(() => this.detectChanges()));
    }

    override ngOnChanges(changes: SimpleChanges) {
        this.isSuccess = false;
        super.ngOnChanges(changes);
    }

    override submit(): void {
        const data = { ...this.data };
        const product = this.model;
        this.requestService.submitForm(
            this.form,
            this.status,
            () => {
                return new Promise<void>((resolve, reject) => {
                    const email = data.email;
                    if (email && product) {
                        const finalize = (consent: boolean) => {
                            this.exponeaService.identifyCustomer(this.customerModel.isLoggedIn ? undefined : email, undefined, email);

                            const finish = () => {
                                if (product?.data?.sku) {
                                    this.exponeaService.sendBackInStockEvent(product.data.sku);
                                }
                                resolve();
                            };

                            if (data.newsletter && !consent) {
                                this.newsletterService.subscribe(
                                    undefined,
                                    undefined,
                                    { email: email, consent: true, source: 'backinstock' },
                                    finish,
                                    finish
                                );
                            } else {
                                finish();
                            }
                        };
                        this.exponeaService.checkConsent(
                            undefined,
                            email,
                            result => {
                                finalize(!!result?.consent);
                            },
                            () => {
                                finalize(false);
                            }
                        );
                    } else {
                        reject();
                    }
                });
            },
            response => this.onSubmitSuccess(response)
        );
    }

    toggleSizeTray(): void {
        this.openSizeTray.emit(true);
    }

    closeAllModals(): void {
        this.modalService.closeAll();
    }

    closeModal(): void {
        this.app.events.broadcast('pageSidebar.close');
    }

    onEmailChange(): void {
        this.checkExponeaConsentDebounced(this.data?.email);
    }

    get showSubscriptionOptIn(): boolean {
        return !this.exponeaConsentResult;
    }

    protected override onSubmitSuccess(response?: any): void {
        this.isSuccess = true;
        super.onSubmitSuccess(response);
    }

    private checkExponeaConsent(email: string): void {
        if (email) {
            this.exponeaService.checkConsent(this.exponeaConsentStatus, email, status => {
                this.exponeaConsentResult = status.consent;
                this.detectChanges();
            });
        } else {
            if (this.exponeaConsentStatus.promise?.cancel) {
                this.exponeaConsentStatus.promise.cancel();
            }
            this.exponeaConsentResult = false;
            this.detectChanges();
        }
    }
}
